import { useState, useEffect, useCallback } from 'react';
import { GOLD, EMBER, MUTE, WARNING, NEON_TEAL, NEON_RED, NEON_GOLD } from '../lib/constants';
import { Ic, Mo, Bd } from './shared';
import { ShareCard } from './ShareCard';
import { LiteracyCard } from './LiteracyCards';
import { DailyChallengeCard } from './DailyChallenges';
import ProgressRing from './ProgressRing';
import * as api from '../lib/api';

const API = process.env.REACT_APP_BACKEND_URL + '/api';

// Get greeting based on time of day
function getGreeting() {
  const hour = new Date().getHours();
  if (hour < 12) return 'Good Morning';
  if (hour < 17) return 'Good Afternoon';
  return 'Good Evening';
}

// Format date
function formatDate() {
  const now = new Date();
  const options = { weekday: 'long', month: 'long', day: 'numeric', year: 'numeric' };
  return now.toLocaleDateString('en-US', options).toUpperCase();
}

// Enhanced Glassmorphism card style with stronger blur and depth
const glassCard = {
  background: 'rgba(255, 255, 255, 0.03)',
  backdropFilter: 'blur(40px) saturate(180%)',
  WebkitBackdropFilter: 'blur(40px) saturate(180%)',
  border: '1px solid rgba(255, 255, 255, 0.08)',
  borderRadius: 16,
  boxShadow: '0 8px 32px 0 rgba(0, 0, 0, 0.37)',
};

// Enhanced Glassmorphism Metrics Card with refined typography
function MetricCard({ label, value, valueColor = '#FFFFFF' }) {
  return (
    <div style={{
      flex: 1,
      padding: '18px 14px',
      ...glassCard,
      textAlign: 'center',
      position: 'relative',
      overflow: 'hidden',
    }}>
      <span style={{
        fontSize: 10,
        fontWeight: 700,
        color: 'rgba(255, 255, 255, 0.45)',
        letterSpacing: '0.08em',
        textTransform: 'uppercase',
        display: 'block',
        marginBottom: 10,
        lineHeight: 1.4,
      }}>{label}</span>
      <span style={{
        fontSize: 24,
        fontWeight: 800,
        color: valueColor,
        letterSpacing: '-0.02em',
        textShadow: valueColor === NEON_TEAL ? '0 0 10px rgba(0, 255, 204, 0.5), 0 0 20px rgba(0, 255, 204, 0.3)' : 
                    valueColor === NEON_RED ? '0 0 10px rgba(255, 77, 77, 0.5), 0 0 20px rgba(255, 77, 77, 0.3)' :
                    valueColor === NEON_GOLD ? '0 0 10px rgba(255, 215, 0, 0.5), 0 0 20px rgba(255, 215, 0, 0.3)' :
                    valueColor === '#E8A849' ? '0 0 15px rgba(232, 168, 73, 0.3)' : 
                    valueColor === '#10B981' ? '0 0 15px rgba(16, 185, 129, 0.3)' : 'none',
      }}>{value}</span>
    </div>
  );
}

// Metrics Row Component
function MetricsRow({ metrics }) {
  return (
    <div data-testid="metrics-row" style={{
      display: 'flex',
      gap: 10,
    }}>
      {metrics.map((m, i) => (
        <MetricCard key={i} label={m.label} value={m.value} valueColor={m.color} />
      ))}
    </div>
  );
}

// Premium Streak Card
function StreakCard({ currentStreak, bestStreak, onShare }) {
  const [heatmap, setHeatmap] = useState([]);
  
  useEffect(() => {
    api.getStreaks().then(r => {
      if (r.data?.heatmap) setHeatmap(r.data.heatmap);
    }).catch(() => {});
  }, []);
  
  // 8 dots for streak visualization
  const dots = Array(8).fill(null).map((_, i) => i < currentStreak);
  
  return (
    <div data-testid="streak-card" style={{
      ...glassCard,
      padding: '20px',
    }}>
      {/* Header */}
      <div style={{
        display: 'flex',
        justifyContent: 'space-between',
        alignItems: 'center',
        marginBottom: 16,
      }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
          <div style={{
            filter: currentStreak > 0 ? 'drop-shadow(0 0 8px rgba(232, 168, 73, 0.6))' : 'none',
          }}>
            {Ic.flame(24, currentStreak > 0 ? '#E8A849' : 'rgba(255, 255, 255, 0.3)')}
          </div>
          <span style={{
            fontSize: 12,
            fontWeight: 700,
            color: '#FFFFFF',
            letterSpacing: '0.12em',
            textTransform: 'uppercase',
          }}>Daily Streak</span>
        </div>
        
        {/* Share Button with enhanced amber glow on hover */}
        <button
          data-testid="share-progress-btn"
          onClick={onShare}
          style={{
            padding: '10px 18px',
            background: 'rgba(255, 255, 255, 0.06)',
            border: '1px solid rgba(255, 255, 255, 0.12)',
            borderRadius: 12,
            color: 'rgba(255, 255, 255, 0.7)',
            fontSize: 12,
            fontWeight: 700,
            letterSpacing: '0.05em',
            cursor: 'pointer',
            display: 'flex',
            alignItems: 'center',
            gap: 8,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={(e) => {
            e.target.style.background = 'rgba(232, 168, 73, 0.18)';
            e.target.style.borderColor = 'rgba(232, 168, 73, 0.4)';
            e.target.style.color = '#E8A849';
            e.target.style.boxShadow = '0 0 25px rgba(232, 168, 73, 0.4), 0 4px 12px rgba(232, 168, 73, 0.2)';
          }}
          onMouseLeave={(e) => {
            e.target.style.background = 'rgba(255, 255, 255, 0.06)';
            e.target.style.borderColor = 'rgba(255, 255, 255, 0.12)';
            e.target.style.color = 'rgba(255, 255, 255, 0.7)';
            e.target.style.boxShadow = 'none';
          }}
        >
          {Ic.send(14, 'currentColor')}
          Share Progress
        </button>
      </div>
      
      {/* Streak Dots with solid amber and enhanced glow */}
      <div style={{
        display: 'flex',
        gap: 10,
        marginBottom: 16,
      }}>
        {dots.map((active, i) => (
          <div
            key={i}
            data-testid={`streak-dot-${i}`}
            style={{
              width: 14,
              height: 14,
              borderRadius: '50%',
              background: active 
                ? NEON_GOLD 
                : 'rgba(255, 255, 255, 0.12)',
              boxShadow: active 
                ? `0 0 16px rgba(255, 215, 0, 0.7), 0 0 8px rgba(255, 215, 0, 0.5)` 
                : 'none',
              transition: 'all 0.3s ease',
            }}
          />
        ))}
      </div>
      
      {/* Streak Label - Large, Bold, and Refined */}
      <span style={{
        fontSize: 28,
        fontWeight: 800,
        color: '#FFFFFF',
        letterSpacing: '-0.02em',
      }}>{currentStreak}-Day Streak!</span>
    </div>
  );
}

// Premium Target Card
function TargetCard({ debt, onClick }) {
  if (!debt) return null;
  
  const progress = debt.original > 0 ? Math.round(((debt.original - debt.balance) / debt.original) * 100) : 0;
  const daysRemaining = Math.ceil(debt.balance / (debt.min_payment || 100));
  const months = Math.floor(daysRemaining / 30);
  
  return (
    <div
      data-testid="target-card"
      onClick={onClick}
      style={{
        display: 'flex',
        gap: 16,
        ...glassCard,
        padding: '20px',
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Countdown Section with subtle glow */}
      <div style={{
        padding: '20px 16px',
        background: 'rgba(255, 255, 255, 0.06)',
        borderRadius: 14,
        minWidth: 100,
        textAlign: 'center',
        border: '1px solid rgba(255, 255, 255, 0.08)',
      }}>
        <span style={{
          fontSize: 36,
          fontWeight: 700,
          color: '#FFFFFF',
          letterSpacing: '-0.02em',
          display: 'block',
          lineHeight: 1,
        }}>{months}mo</span>
        <span style={{
          fontSize: 12,
          color: 'rgba(255, 255, 255, 0.5)',
          display: 'block',
          marginTop: 6,
          letterSpacing: '0.04em',
        }}>Remaining</span>
      </div>
      
      {/* Details Section */}
      <div style={{ flex: 1, display: 'flex', flexDirection: 'column', justifyContent: 'center', gap: 6 }}>
        <span style={{
          fontSize: 18,
          fontWeight: 700,
          color: '#FFFFFF',
        }}>{debt.name}</span>
        <span style={{
          fontSize: 14,
          color: 'rgba(255, 255, 255, 0.5)',
          lineHeight: 1.4,
        }}>Target: ${debt.balance.toLocaleString()} · {debt.apr}% APR</span>
        
        {/* On Track Badge with glow */}
        <div style={{
          display: 'inline-flex',
          alignItems: 'center',
          gap: 8,
          padding: '8px 14px',
          background: 'rgba(16, 185, 129, 0.15)',
          borderRadius: 10,
          width: 'fit-content',
          marginTop: 4,
          boxShadow: '0 0 15px rgba(16, 185, 129, 0.2)',
        }}>
          <div style={{
            width: 8,
            height: 8,
            borderRadius: '50%',
            background: '#10B981',
            boxShadow: '0 0 8px #10B981',
          }} />
          <span style={{
            fontSize: 13,
            fontWeight: 600,
            color: '#10B981',
          }}>On Track</span>
        </div>
      </div>
    </div>
  );
}

// Premium Action Buttons Grid
function ActionButtonsGrid({ navigate }) {
  const actions = [
    { icon: 'calendar', label: 'View Plan', action: () => navigate('plan') },
    { icon: 'plus', label: 'Add Debt', action: () => navigate('debt-entry') },
    { icon: 'bolt', label: 'Quick Fix', action: () => navigate('insights') },
    { icon: 'chart', label: 'Reports', action: () => navigate('insights') },
  ];
  
  return (
    <div data-testid="action-buttons" style={{
      display: 'grid',
      gridTemplateColumns: 'repeat(4, 1fr)',
      gap: 10,
    }}>
      {actions.map((a, i) => (
        <button
          key={i}
          data-testid={`action-btn-${i}`}
          onClick={a.action}
          style={{
            padding: '20px 10px',
            ...glassCard,
            cursor: 'pointer',
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            gap: 10,
            transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
          }}
          onMouseEnter={(e) => {
            e.currentTarget.style.background = 'rgba(232, 168, 73, 0.12)';
            e.currentTarget.style.borderColor = 'rgba(232, 168, 73, 0.35)';
            e.currentTarget.style.boxShadow = '0 0 30px rgba(232, 168, 73, 0.25), 0 8px 24px rgba(232, 168, 73, 0.15)';
            e.currentTarget.style.transform = 'translateY(-2px)';
          }}
          onMouseLeave={(e) => {
            e.currentTarget.style.background = 'rgba(255, 255, 255, 0.03)';
            e.currentTarget.style.borderColor = 'rgba(255, 255, 255, 0.08)';
            e.currentTarget.style.boxShadow = '0 8px 32px 0 rgba(0, 0, 0, 0.37)';
            e.currentTarget.style.transform = 'translateY(0)';
          }}
        >
          <div style={{ 
            color: '#E8A849',
            filter: 'drop-shadow(0 0 10px rgba(232, 168, 73, 0.5)) drop-shadow(0 0 4px rgba(232, 168, 73, 0.3))',
          }}>
            {a.icon === 'calendar' && Ic.calendar(28, '#E8A849')}
            {a.icon === 'plus' && Ic.plus(28, '#E8A849')}
            {a.icon === 'bolt' && Ic.bolt(28, '#E8A849')}
            {a.icon === 'chart' && Ic.chart(28, '#E8A849')}
          </div>
          <span style={{
            fontSize: 11,
            fontWeight: 700,
            color: 'rgba(255, 255, 255, 0.85)',
            letterSpacing: '0.05em',
            textAlign: 'center',
          }}>{a.label}</span>
        </button>
      ))}
    </div>
  );
}

// Premium Dashboard Header
function DashboardHeader({ user }) {
  return (
    <div data-testid="dashboard-header" style={{
      padding: '52px 20px 36px',
      position: 'relative',
    }}>
      {/* Top bar with notifications and avatar */}
      <div style={{
        position: 'absolute',
        top: 16,
        right: 20,
        display: 'flex',
        alignItems: 'center',
        gap: 16,
      }}>
        {/* Notification with glow */}
        <button data-testid="notification-bell" style={{
          background: 'transparent',
          border: 'none',
          cursor: 'pointer',
          position: 'relative',
          padding: 4,
        }}>
          {Ic.bell(24, 'rgba(255, 255, 255, 0.6)')}
          <span style={{
            position: 'absolute',
            top: 2,
            right: 2,
            width: 10,
            height: 10,
            borderRadius: '50%',
            background: '#E8A849',
            boxShadow: '0 0 8px rgba(232, 168, 73, 0.6)',
          }} />
        </button>
        
        {/* Avatar with border */}
        <div style={{
          width: 40,
          height: 40,
          borderRadius: '50%',
          background: 'linear-gradient(135deg, rgba(255, 255, 255, 0.1), rgba(255, 255, 255, 0.05))',
          border: '2px solid rgba(255, 255, 255, 0.2)',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          fontSize: 15,
          fontWeight: 600,
          color: 'rgba(255, 255, 255, 0.8)',
        }}>
          {user?.name?.charAt(0)?.toUpperCase() || 'U'}
        </div>
      </div>
      
      {/* Main greeting - Enhanced typography */}
      <h1 style={{
        fontSize: 34,
        fontWeight: 800,
        color: '#FFFFFF',
        letterSpacing: '-0.02em',
        marginBottom: 8,
        lineHeight: 1.15,
      }}>{getGreeting()}, {user?.name?.split(' ')[0] || 'there'}.</h1>
      
      {/* Subtitle - refined color */}
      <p style={{
        fontSize: 17,
        color: 'rgba(255, 255, 255, 0.5)',
        marginBottom: 12,
        fontWeight: 400,
        letterSpacing: '-0.01em',
      }}>Let's check your progress.</p>
      
      {/* Date - uppercase, amber, enhanced spacing */}
      <span style={{
        fontSize: 11,
        fontWeight: 700,
        color: '#E8A849',
        letterSpacing: '0.12em',
        textTransform: 'uppercase',
      }}>{formatDate()}</span>
    </div>
  );
}

// Premium Debt Row
function DebtRow({ debt, onClick }) {
  const progress = debt.original > 0 ? Math.round(((debt.original - debt.balance) / debt.original) * 100) : 0;
  
  return (
    <div
      data-testid={`debt-row-${debt.id}`}
      onClick={onClick}
      className={debt.is_target ? 'target-card-active' : ''}
      style={{
        display: 'flex',
        alignItems: 'center',
        gap: 14,
        padding: '16px 18px',
        ...glassCard,
        marginBottom: 10,
        cursor: 'pointer',
        transition: 'all 0.2s ease',
      }}
    >
      {/* Icon container */}
      <div style={{
        width: 46,
        height: 46,
        borderRadius: 12,
        background: 'rgba(255, 255, 255, 0.08)',
        border: '1px solid rgba(255, 255, 255, 0.1)',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        flexShrink: 0,
      }}>
        {debt.type === 'credit' ? Ic.credit(22, 'rgba(255, 255, 255, 0.6)') : 
         debt.type === 'auto' ? Ic.car(22, 'rgba(255, 255, 255, 0.6)') : 
         Ic.loan(22, 'rgba(255, 255, 255, 0.6)')}
      </div>
      
      {/* Content */}
      <div style={{ flex: 1 }}>
        <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
          <span style={{
            fontSize: 16,
            fontWeight: 600,
            color: '#FFFFFF',
          }}>{debt.name}</span>
          {debt.is_target && (
            <span style={{
              padding: '4px 10px',
              background: 'linear-gradient(135deg, rgba(6, 182, 212, 0.25) 0%, rgba(16, 185, 129, 0.25) 100%)',
              border: '1px solid rgba(6, 182, 212, 0.4)',
              borderRadius: 8,
              fontSize: 10,
              fontWeight: 700,
              color: '#06B6D4',
              letterSpacing: '0.06em',
              boxShadow: '0 0 15px rgba(6, 182, 212, 0.3)',
              textShadow: '0 0 10px rgba(6, 182, 212, 0.5)',
            }}>TARGET</span>
          )}
        </div>
        
        {/* Progress Bar with glow */}
        <div style={{
          height: 5,
          background: 'rgba(255, 255, 255, 0.1)',
          borderRadius: 3,
          overflow: 'hidden',
        }}>
          <div style={{
            height: '100%',
            width: `${progress}%`,
            background: debt.is_target
              ? 'linear-gradient(90deg, #D4880F, #E8A849, #FFD066)'
              : 'rgba(255, 255, 255, 0.4)',
            borderRadius: 3,
            boxShadow: debt.is_target ? '0 0 10px rgba(232, 168, 73, 0.4)' : 'none',
          }} />
        </div>
      </div>
      
      {/* Balance */}
      <span style={{
        fontSize: 17,
        fontWeight: 700,
        color: '#FFFFFF',
        flexShrink: 0,
      }}>${debt.balance.toLocaleString()}</span>
    </div>
  );
}

// Section Label
function SectionLabel({ children }) {
  return (
    <span style={{
      fontSize: 11,
      fontWeight: 700,
      color: 'rgba(255, 255, 255, 0.4)',
      letterSpacing: '0.12em',
      textTransform: 'uppercase',
      display: 'block',
      marginBottom: 14,
    }}>{children}</span>
  );
}

// Main Home Screen Component
export function HomeScreen({ navigate, debts, user }) {
  const [showShare, setShowShare] = useState(false);
  const [literacyCard, setLiteracyCard] = useState(null);

  const fetchRandomCard = useCallback(() => {
    fetch(`${API}/literacy/random?count=1`).then(r => r.json()).then(d => setLiteracyCard(d[0])).catch(() => {});
  }, []);

  useEffect(() => { fetchRandomCard(); }, [fetchRandomCard]);
  
  const totalDebt = debts.reduce((s, d) => s + d.balance, 0);
  const totalOrig = debts.reduce((s, d) => s + d.original, 0);
  const killed = totalOrig - totalDebt;
  const pct = totalOrig > 0 ? Math.round((killed / totalOrig) * 100) : 0;
  
  // Find target debt
  const targetDebt = debts.find(d => d.is_target) || debts[0];
  
  // Calculate metrics
  const avgApr = debts.length > 0 ? (debts.reduce((s, d) => s + (d.apr || 0), 0) / debts.length).toFixed(1) : 0;
  
  const metrics = [
    { label: 'Debt\nKilled', value: `$${(killed / 1000).toFixed(1)}k`, color: NEON_TEAL }, // Electric Teal with glow
    { label: 'Active\nAccounts', value: `${debts.length}`, color: '#FFFFFF' },
    { label: 'Avg\nAPR', value: `${avgApr}%`, color: avgApr > 15 ? NEON_RED : '#FFFFFF' }, // Red if high APR
  ];

  return (
    <div data-testid="home-screen" style={{
      // Enhanced deep navy/blue-black gradient background with more depth
      background: 'linear-gradient(180deg, #0A0F1A 0%, #070B14 40%, #05090F 70%, #030609 100%)',
      minHeight: '100vh',
      paddingBottom: 100,
      position: 'relative',
    }}>
      {/* Enhanced radial gradient overlay for depth with more prominence */}
      <div style={{
        position: 'fixed',
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        background: 'radial-gradient(ellipse 90% 60% at 50% 0%, rgba(232, 168, 73, 0.06) 0%, rgba(232, 168, 73, 0.02) 30%, transparent 60%)',
        pointerEvents: 'none',
        zIndex: 0,
      }} />
      
      {/* Content */}
      <div style={{ position: 'relative', zIndex: 1 }}>
        {/* Header */}
        <DashboardHeader user={user} />
        
        {/* Main Content */}
        <div style={{ padding: '0 20px' }}>
          {/* Progress Ring with ambient glow */}
          <div style={{ 
            display: 'flex', 
            justifyContent: 'center', 
            marginBottom: 28,
            paddingTop: 8,
          }}>
            <ProgressRing percent={pct} size={200} />
          </div>
          
          {/* Metrics Row */}
          <div style={{ marginBottom: 20 }}>
            <MetricsRow metrics={metrics} />
          </div>
          
          {/* Streak Card */}
          <div style={{ marginBottom: 20 }}>
            <StreakCard 
              currentStreak={user?.streak || 0} 
              bestStreak={user?.streak || 0}
              onShare={() => setShowShare(true)}
            />
          </div>
          
          {/* Target Card */}
          {targetDebt && (
            <div style={{ marginBottom: 20 }}>
              <TargetCard 
                debt={targetDebt}
                onClick={() => navigate('debt-detail', null, targetDebt)}
              />
            </div>
          )}
          
          {/* Action Buttons */}
          <div style={{ marginBottom: 24 }}>
            <ActionButtonsGrid navigate={navigate} />
          </div>
          
          {/* Share Modal */}
          {showShare && (
            <ShareCard 
              type="streak" 
              stats={{ 
                streak: user?.streak || 0, 
                paidThisStreak: `$${killed.toLocaleString(undefined, { maximumFractionDigits: 0 })}`, 
                debtsActive: `${debts.length}`, 
                progress: `${pct}%`, 
                shareText: `${user?.streak || 0} day streak on Dettio! ${pct}% debt free.` 
              }} 
              onClose={() => setShowShare(false)} 
            />
          )}
          
          {/* All Debts Section */}
          {debts.length > 0 && (
            <div style={{ marginBottom: 24 }}>
              <SectionLabel>Your Accounts</SectionLabel>
              {debts.map(d => (
                <DebtRow 
                  key={d.id} 
                  debt={d} 
                  onClick={() => navigate('debt-detail', null, d)} 
                />
              ))}
            </div>
          )}
          
          {/* Daily Challenge */}
          <div style={{ marginBottom: 24 }}>
            <DailyChallengeCard onViewAll={() => navigate('challenges')} />
          </div>
          
          {/* Did You Know */}
          <div style={{ marginBottom: 24 }}>
            <SectionLabel>Did You Know</SectionLabel>
            <LiteracyCard card={literacyCard} onRefresh={fetchRandomCard} onViewAll={() => navigate('literacy')} />
          </div>
          
          <div style={{ height: 40 }} />
        </div>
      </div>
    </div>
  );
}
